import Delta from "quill-delta";
import { canv } from "@/canv/global";
import { getContrastColor, lightOrDark } from "@/canv/utils";
import { ShapeWidget } from "@/elements/shape";
import { stickerWidget } from "@/elements/sticker";
import { selectionManager } from "@/tools/cursorTool";
import { QuillEditor } from "./quillEditor";
import { TextWidgetV2 } from "./textV2";
import type { LimitNoticeKind, QuillFormats, ResizeResult, SelectionSummary } from "./typesV2";
import { isUIElement, sanitizeDeltaAlign, sceneToClient } from "./utilsV2";

export class TextEditingController {
    private textWidget: TextWidgetV2 | null = null;
    private quillEditor: QuillEditor;
    private removeClickOutside: (() => void) | null = null;
    private removeEditorLinkListeners: (() => void) | null = null;
    private removeEditorContextMenu: (() => void) | null = null;

    constructor() {
        const overlayRoot = document.querySelector("#canvas-overlay-root") as HTMLDivElement;

        this.quillEditor = new QuillEditor({
            overlayRoot,
            onSize: this.onEditorTextChange,
            onSelectionChange: this.onEditorSelectionChange,
            onFontSizeChange: this.onFontSizeChange,
            onPasteApplied: () => this.applyPostPasteFormatting(),
            getCurrentTextData: this.getCurrentTextData,
        });
    }

    getIsEditing() {
        return this.textWidget !== null;
    }

    isEditingWidget(textWidget: TextWidgetV2) {
        return this.textWidget?.UID === textWidget.UID;
    }

    startEdit(textWidget: TextWidgetV2, focusAtPointer = false) {
        if (this.textWidget?.UID === textWidget.UID) {
            return;
        }

        if (this.textWidget) {
            this.commit();
        }

        this.textWidget = textWidget;

        const domLineHeightPx = this.textWidget.lineHeight;

        const { fontSize, fontFamily, textAlign, delta } = this.textWidget.getData();

        this.quillEditor.open(
            {
                fontSize,
                lineHeight: domLineHeightPx,
                fontFamily,
                textAlign,
                color: this.textWidget.getCurrentColor(),
                horizontalPadding: this.textWidget.horizontalPadding,
            },
            delta,
        );

        if (this.textWidget.resizeMode === "shape" || this.textWidget.resizeMode === "sticker") {
            this.quillEditor.setResizeOptions({
                mode: this.textWidget.resizeMode,
                emptyWidth: this.textWidget.getEmptyInputWidth(),
                paddingX: this.textWidget.horizontalPadding,
                fixedWidth: this.textWidget.hardWidth,
                fixedHeight: this.textWidget.hardHeight,
                currentFontSize: fontSize,
            });
        } else {
            this.quillEditor.setResizeOptions({
                mode: this.textWidget.resizeMode,
                emptyWidth: this.textWidget.getEmptyInputWidth(),
                paddingX: this.textWidget.horizontalPadding,
            });
        }

        this.bindOverlayEvents();
        this.updateOverlayTransform();
        this.applyFocusMode(focusAtPointer);
        canv.appInterface.linkPopupController.attachEditorSession(
            this.textWidget.UID,
            this.quillEditor.getQuill(),
        );
        canv.appInterface.linkPopupController.onEditorSelectionChange();

        // Для пустого текста явно задаем активный цвет в Quill после выставления фокуса/каретки,
        // чтобы первый ввод сохранялся с текущим цветом.
        if (this.textWidget.symbolsCount === 0) {
            this.quillEditor.applyTextFormatting("color", this.textWidget.getCurrentColor());
        }

        canv.appInterface.inputFocused = 1;

        this.installClickOutside();
        this.installEditorLinkListeners();
        this.installEditorContextMenu();
    }

    commit() {
        if (!this.textWidget) {
            return;
        }

        if (!this.quillEditor.getIsTextChanged()) {
            this.cleanup();
            return;
        }

        this.textWidget.aiGenerated = false;

        const next = this.quillEditor.getDelta();

        this.textWidget.setNewDelta(next);

        this.cleanup();
    }

    cancel() {
        if (!this.textWidget) {
            return;
        }
        this.cleanup();
    }

    updateOverlayTransform(newWidth = 0) {
        if (!this.textWidget) {
            return;
        }

        this.syncResizeOptionsFromWidget();

        const zoom = canv.m[0] / canv.dpr;

        // World координаты
        const worldX = this.textWidget.startX;
        const worldY = this.textWidget.startY - this.textWidget.lineHeight;

        // Viewport координаты
        const { clientX, clientY } = sceneToClient({ x: worldX, y: worldY }, canv.m, canv.dpr);

        const width = newWidth || this.textWidget.width;
        const height = this.textWidget.height;

        // Компенсация transform-origin: center center
        const xAdjusted = clientX + (width * (zoom - 1)) / 2;
        const yAdjusted = clientY + (height * (zoom - 1)) / 2;

        const rotation = canv.canvasController.selected.length
            ? -canv.canvasController.selected[0].angle
            : 0;

        const { fontSize, fontFamily, textAlign } = this.textWidget.getData();

        this.quillEditor.updateTransform({
            x: xAdjusted,
            y: yAdjusted,
            width,
            height,
            scale: zoom,
            fontSize,
            lineHeight: this.textWidget.lineHeight,
            fontFamily,
            horizontalPadding: this.textWidget.horizontalPadding,
            textAlign,
            rotation,
        });

        canv.appInterface.linkPopupController.onViewportTransformChange();
    }

    applyTextFormatting(name: QuillFormats, value: string | boolean) {
        this.quillEditor.applyTextFormatting(name, value);
    }

    applyTextFormattingToAll(name: QuillFormats, value: string | boolean) {
        this.quillEditor.applyTextFormattingToAll(name, value);
    }

    insertText(text: string, replaceAll = false) {
        if (!this.textWidget || !text) {
            return;
        }

        const quill = this.quillEditor.getQuill();
        this.quillEditor.focus();

        if (replaceAll) {
            const fullLength = Math.max(0, quill.getLength() - 1);
            quill.setSelection(0, fullLength, "silent");
        }

        this.quillEditor.replaceSelectionWithText(text);
    }

    insertDelta(delta: Delta, replaceAll = false, limitKind: LimitNoticeKind = "typing") {
        if (!this.textWidget) {
            return;
        }

        const quill = this.quillEditor.getQuill();
        this.quillEditor.focus();

        if (replaceAll) {
            const fullLength = Math.max(0, quill.getLength() - 1);
            quill.setSelection(0, fullLength, "silent");
        }

        this.quillEditor.replaceSelectionWithDelta(delta, limitKind);
    }

    insertHtml(html: string, replaceAll = false, limitKind: LimitNoticeKind = "paste") {
        const delta = this.convertPasteHtmlToDelta(html);
        const sanitizedDelta = sanitizeDeltaAlign(delta);

        this.insertDelta(sanitizedDelta, replaceAll, limitKind);
        this.applyPostPasteFormatting();
    }

    insertPastedText(text: string, replaceAll = false) {
        if (!text) {
            return;
        }

        this.insertDelta(new Delta().insert(text), replaceAll, "paste");
        this.applyPostPasteFormatting();
    }

    async pasteFromClipboard(replaceAll = false): Promise<void> {
        if (!this.textWidget) {
            return;
        }

        const quill = this.quillEditor.getQuill();
        this.quillEditor.focus();

        if (replaceAll) {
            const fullLength = Math.max(0, quill.getLength() - 1);
            quill.setSelection(0, fullLength, "silent");
        }

        await this.quillEditor.pasteFromClipboard();
        this.applyPostPasteFormatting();
    }

    patchDelta(name: QuillFormats, value: string | boolean, delta: Delta) {
        return this.quillEditor.patchDelta(name, value, delta);
    }

    getSelection() {
        return this.quillEditor.getSelection();
    }

    convertPasteHtmlToDelta(html: string): Delta {
        return this.quillEditor.convertHtmlToDeltaForPaste(html);
    }

    async handleContextMenuEditCommand(command: "copy" | "paste" | "delete"): Promise<void> {
        if (!this.textWidget) {
            return;
        }

        this.quillEditor.focus();

        switch (command) {
            case "copy": {
                await this.quillEditor.copySelectionToClipboard();
                return;
            }
            case "paste": {
                await this.pasteFromClipboard();
                return;
            }
            case "delete": {
                this.quillEditor.deleteSelectionOrBackward();
                return;
            }
        }
    }

    showSelectionRects() {
        const scale = canv.m[0] / canv.dpr;

        this.quillEditor.showSelectionRects(scale);
    }

    hideSelectionRects() {
        this.quillEditor.hideSelectionRects();
    }

    private bindOverlayEvents() {
        this.quillEditor.onKeyDown = (event) => {
            if (canv.contextMenu.isOpened) {
                canv.contextMenu.hide();
            }

            event.stopPropagation();

            if (event.key === "Escape") {
                event.preventDefault();
                this.commit();
                return;
            }

            if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                event.preventDefault();
                this.commit();
                return;
            }
        };
    }

    private applyFocusMode(focusAtPointer = false) {
        if (focusAtPointer) {
            const pt = canv.actionManager.getActionPoint();
            const { clientX, clientY } = sceneToClient(pt, canv.m, canv.dpr);
            this.quillEditor.focusAtPoint(clientX, clientY);
            return;
        }

        this.quillEditor.focusToEnd();
    }

    private installClickOutside() {
        const handler = (event: PointerEvent) => {
            if (
                selectionManager.isOverSide() ||
                selectionManager.isOverCatchers() ||
                selectionManager.isOverRotater()
            ) {
                event.preventDefault();
                return;
            }

            if (canv.appInterface.linkInputController.isInputVisible) {
                return;
            }

            const target = event.target as Node | null;
            if (!target) {
                return;
            }

            if (this.quillEditor.contains(target)) {
                return;
            }

            const el = event.target as Element | null;
            if (el && isUIElement(el)) {
                return;
            }

            this.commit();
        };

        document.addEventListener("pointerdown", handler, true);
        this.removeClickOutside = () => {
            document.removeEventListener("pointerdown", handler, true);
        };
    }

    private installEditorContextMenu() {
        const handler = (e: MouseEvent) => {
            const target = e.target as Node | null;
            if (!target || !this.quillEditor.contains(target)) {
                return;
            }

            const canvasRect = canv.canvas.getBoundingClientRect();
            canv.actionManager.lastX = e.clientX - canvasRect.left;
            canv.actionManager.lastY = e.clientY - canvasRect.top;

            e.preventDefault();
            e.stopPropagation();

            canv.contextMenu.show();
            canv.redraw();
        };

        document.addEventListener("contextmenu", handler, true);
        this.removeEditorContextMenu = () => {
            document.removeEventListener("contextmenu", handler, true);
        };
    }

    private cleanup() {
        if (!this.textWidget) {
            return;
        }

        this.removeClickOutside?.();
        this.removeClickOutside = null;
        this.removeEditorLinkListeners?.();
        this.removeEditorLinkListeners = null;
        this.removeEditorContextMenu?.();
        this.removeEditorContextMenu = null;

        this.textWidget.commitDefferedChanges();

        this.quillEditor.close();
        canv.appInterface.linkPopupController.detachEditorSession();

        if (this.textWidget.getIsEditing()) {
            canv.redraw();
            this.textWidget.stopInteraction();
        }

        const isWidgetSelected = canv.canvasController.selected.length;

        if (isWidgetSelected && this.textWidget.resizeMode === "shape") {
            const shape = canv.canvasController.selected[0].children[0];

            shape.updateDims();
        }

        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");

        this.textWidget = null;

        canv.appInterface.inputFocused = 0;
    }

    private onEditorTextChange = ({ width, height, mode, fixedWidth }: ResizeResult) => {
        if (!this.textWidget) {
            return;
        }

        const prevMode = this.textWidget.resizeMode;

        this.textWidget.setResizeMode(mode);

        if (prevMode === "auto" && mode === "shape" && fixedWidth !== undefined) {
            this.textWidget.hardWidth = fixedWidth;
        }
        this.textWidget.width = width;
        this.textWidget.height = height;

        this.textWidget.updateBase(
            this.textWidget.startX,
            this.textWidget.startY - this.textWidget.lineHeight,
            this.textWidget.width,
            this.textWidget.height,
        );

        const selectedWidget = canv.canvasController.selected[0]?.children?.[0];

        if (!(selectedWidget instanceof TextWidgetV2)) {
            selectedWidget.TryAction("keyboardDown");
        }

        canv.redraw();
        this.updateOverlayTransform();
        canv.appInterface.linkPopupController.onEditorTextChange();
    };

    private onEditorSelectionChange = (summary: SelectionSummary) => {
        if (!this.textWidget) {
            return;
        }

        this.textWidget.setStyleSummary(summary);
        canv.widgetMenu.recollectWidgetOptionsMap();
        canv.appInterface.linkPopupController.onEditorSelectionChange();
    };

    private applyPostPasteFormatting() {
        if (!this.textWidget) {
            return;
        }

        switch (this.textWidget.resizeMode) {
            case "shape":
                this.applyShapePasteFormatting();
                break;
            case "sticker":
                this.applyStickerPasteFormatting();
                break;
            default:
                break;
        }
    }

    private getStickerTextColor(): string | null {
        const selectedWidget = canv.canvasController.selected[0]?.children?.[0];
        if (selectedWidget instanceof stickerWidget) {
            return getContrastColor(selectedWidget.color);
        }

        return null;
    }

    private applyStickerPasteFormatting() {
        const color = this.getStickerTextColor();
        if (!color) {
            return;
        }

        this.quillEditor.applyTextFormattingToAll("list", false);
        this.quillEditor.applyTextFormattingToAll("color", color);
    }

    private getShapeTextColor(): string | null {
        const selectedWidget = canv.canvasController.selected[0]?.children?.[0];

        if (selectedWidget instanceof ShapeWidget) {
            return lightOrDark(selectedWidget.color) === "light" ? "black" : "white";
        }

        return null;
    }

    private applyShapePasteFormatting() {
        this.quillEditor.applyTextFormattingToAll("list", false);

        const color = this.getShapeTextColor();
        if (!color) {
            return;
        }

        this.quillEditor.applyTextFormattingToAll("color", color);
    }

    private onFontSizeChange = (fontSize: number) => {
        if (!this.textWidget) {
            return;
        }

        this.textWidget.menuTextSize(fontSize);
    };

    private getCurrentTextData = () => {
        if (!this.textWidget) {
            throw new Error("Отсутствует активный textWidget");
        }

        return {
            ...this.textWidget.getData(),
            delta: this.quillEditor.getDelta(),
        };
    };

    private installEditorLinkListeners() {
        const root = this.quillEditor.quill.getEditor();
        const controller = canv.appInterface.linkPopupController;

        const resolveLinkElement = (target: EventTarget | null): HTMLAnchorElement | null => {
            if (!(target instanceof Element)) {
                return null;
            }

            return target.closest("a");
        };

        const handlePointerMove = (event: PointerEvent) => {
            const link = resolveLinkElement(event.target);
            if (!link || !this.textWidget) {
                controller.onEditorPointerLinkLeave();
                return;
            }

            controller.onEditorPointerLinkEnterByElement(link);
        };

        const handlePointerLeave = () => {
            controller.onEditorPointerLinkLeave();
        };

        const handlePointerDown = (event: PointerEvent) => {
            const link = resolveLinkElement(event.target);
            if (!link || !this.textWidget) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            controller.onEditorLinkClickByElement(link);
        };

        root.addEventListener("pointermove", handlePointerMove);
        root.addEventListener("pointerleave", handlePointerLeave);
        root.addEventListener("pointerdown", handlePointerDown, true);

        this.removeEditorLinkListeners = () => {
            root.removeEventListener("pointermove", handlePointerMove);
            root.removeEventListener("pointerleave", handlePointerLeave);
            root.removeEventListener("pointerdown", handlePointerDown, true);
        };
    }

    private syncResizeOptionsFromWidget() {
        if (!this.textWidget) {
            return;
        }

        const { fontSize } = this.textWidget.getData();

        this.quillEditor.setResizeOptions({
            mode: this.textWidget.resizeMode,
            emptyWidth: this.textWidget.getEmptyInputWidth(),
            paddingX: this.textWidget.horizontalPadding,
            fixedWidth: this.textWidget.hardWidth,
            fixedHeight: this.textWidget.hardHeight,
            currentFontSize: fontSize,
        });
    }

    syncEditorAfterStyleChange() {
        if (!this.textWidget) {
            return;
        }

        this.syncResizeOptionsFromWidget();
        this.updateOverlayTransform();
        this.quillEditor.remeasure();
    }

    getIsTextChanged() {
        return this.quillEditor.getIsTextChanged();
    }
}
